import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Symbol Coding — FTUE demo (3 essais pédagogiques).
 *
 * Step 1 : montre la "key" 9 symboles statique.
 * Step 2 : digit présenté, user clique parmi 4 options.
 * Step 3 : trial proche du staircase réel.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SYMBOLS_KEY } from './adapter';
// Key fixe pour la démo : digit (1-9) → index dans SYMBOLS_KEY.
const DEMO_KEY = [0, 1, 2, 3, 4, 5, 6, 7, 8];
const DEMO_STEPS = [
    {
        idx: 1,
        titleKey: 'symbol_coding.demo.step1_title',
        hintKey: 'symbol_coding.demo.step1_hint',
        showKeyOnly: true,
    },
    {
        idx: 2,
        titleKey: 'symbol_coding.demo.step2_title',
        hintKey: 'symbol_coding.demo.step2_hint',
        showKeyOnly: false,
        digit: 3,
    },
    {
        idx: 3,
        titleKey: 'symbol_coding.demo.step3_title',
        hintKey: 'symbol_coding.demo.step3_hint',
        showKeyOnly: false,
        digit: 7,
    },
];
function buildOptions(correctSymbolIdx) {
    const pool = [];
    for (let i = 0; i < SYMBOLS_KEY.length; i += 1) {
        if (i !== correctSymbolIdx)
            pool.push(i);
    }
    // Shuffle simple.
    for (let i = pool.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1));
        const tmp = pool[i];
        pool[i] = pool[j];
        pool[j] = tmp;
    }
    const opts = [correctSymbolIdx, ...pool.slice(0, 3)];
    for (let i = opts.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1));
        const tmp = opts[i];
        opts[i] = opts[j];
        opts[j] = tmp;
    }
    return opts;
}
export function Demo({ onComplete, onSkip }) {
    const { t } = useTranslation(['games', 'common']);
    const [stepIdx, setStepIdx] = useState(0);
    const [phase, setPhase] = useState('briefing');
    const [picked, setPicked] = useState(null);
    const fixationTimer = useRef(null);
    const step = DEMO_STEPS[stepIdx];
    const isLast = stepIdx === DEMO_STEPS.length - 1;
    // Options stables pour ce step (générées au mount).
    const correctSymbolIdx = useMemo(() => {
        if (step.showKeyOnly || step.digit === undefined)
            return -1;
        return DEMO_KEY[step.digit - 1] ?? 0;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stepIdx]);
    const options = useMemo(() => {
        if (step.showKeyOnly || correctSymbolIdx === -1)
            return [];
        return buildOptions(correctSymbolIdx);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stepIdx, correctSymbolIdx]);
    useEffect(() => {
        setPhase('briefing');
        setPicked(null);
        if (fixationTimer.current)
            clearTimeout(fixationTimer.current);
        return () => {
            if (fixationTimer.current)
                clearTimeout(fixationTimer.current);
        };
    }, [stepIdx]);
    const handleStart = () => {
        if (step.showKeyOnly) {
            // Step 1 : pas d'interaction, on avance direct.
            handleNext();
            return;
        }
        setPhase('fixation');
        fixationTimer.current = setTimeout(() => setPhase('awaiting'), 400);
    };
    const handlePick = (idx) => {
        if (phase !== 'awaiting')
            return;
        setPicked(idx);
        setPhase('feedback');
    };
    const handleNext = () => {
        if (isLast) {
            onComplete();
            return;
        }
        setStepIdx((i) => i + 1);
    };
    const correct = picked === correctSymbolIdx;
    return (_jsxs("div", { className: "flex flex-1 flex-col items-center gap-s4 py-s4 text-center", children: [_jsx("p", { className: "font-mono text-[9.5px] uppercase tracking-[0.30em] text-cyan-text-faint", children: t('common:demo.eyebrow_with_master', { master: t('symbol_coding.demo.master') }) }), _jsx("h2", { className: "font-display text-[22px] font-medium leading-7 tracking-cy-display text-cyan-text", children: t(step.titleKey) }), _jsx("p", { className: "max-w-[320px] text-[13.5px] text-cyan-text-secondary", children: t(step.hintKey) }), step.showKeyOnly ? (_jsx("div", { className: "grid w-full max-w-[280px] grid-cols-3 gap-s2 border border-cyan-border bg-cyan-surface p-s3", children: DEMO_KEY.map((symIdx, i) => (_jsxs("div", { className: "flex flex-col items-center gap-s1 border border-cyan-border-faint bg-cyan-bg p-s2", children: [_jsx("span", { className: "font-display text-[28px] text-cyan-text", children: SYMBOLS_KEY[symIdx] }), _jsx("span", { className: "font-mono text-[11px] text-cyan-text-secondary", children: i + 1 })] }, `key-${i}`))) })) : (_jsxs(_Fragment, { children: [_jsx("div", { className: "flex w-full max-w-[300px] flex-wrap items-center justify-center gap-s1 border border-cyan-border-faint bg-cyan-surface p-s2", children: DEMO_KEY.map((symIdx, i) => (_jsxs("div", { className: "flex items-center gap-s1 px-s2 py-s1", children: [_jsx("span", { className: "font-display text-[16px] text-cyan-text", children: SYMBOLS_KEY[symIdx] }), _jsx("span", { className: "font-mono text-[10px] text-cyan-text-secondary", children: i + 1 })] }, `mini-${i}`))) }), _jsxs("div", { className: "flex h-[120px] w-full max-w-[280px] items-center justify-center", style: { background: '#1a1a1a' }, children: [phase === 'briefing' && (_jsx("span", { className: "font-display text-[14px] text-cyan-surface opacity-60", children: t('symbol_coding.demo.ready') })), phase === 'fixation' && (_jsx("span", { className: "font-display text-[36px] text-cyan-surface", children: "+" })), (phase === 'awaiting' || phase === 'feedback') && step.digit !== undefined && (_jsx("span", { className: "font-display text-[64px] text-cyan-surface", children: step.digit }))] }), phase === 'awaiting' && (_jsx("div", { className: "grid w-full max-w-[280px] grid-cols-4 gap-s2", children: options.map((idx) => (_jsx("button", { type: "button", onClick: () => handlePick(idx), "aria-label": t('symbol_coding.option_aria', { symbol: SYMBOLS_KEY[idx] }), className: "flex h-[56px] items-center justify-center border border-cyan-border bg-cyan-surface font-display text-[28px] text-cyan-text hover:bg-cyan-bg active:scale-95", children: SYMBOLS_KEY[idx] }, `opt-${idx}`))) })), phase === 'feedback' && (_jsx("span", { className: [
                            'font-display text-[18px] font-medium',
                            correct ? 'text-cyan-core' : 'text-cyan-media',
                        ].join(' '), children: correct
                            ? `✓ ${t('symbol_coding.feedback.correct')}`
                            : `✗ ${t('symbol_coding.feedback.incorrect')}` }))] })), _jsxs("div", { className: "flex flex-col items-stretch gap-s3 self-stretch", children: [phase === 'briefing' && (_jsx("button", { type: "button", onClick: handleStart, className: "inline-flex h-12 w-full items-center justify-center bg-cyan-core font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-surface transition active:scale-[0.985] hover:bg-cyan-network", children: step.showKeyOnly
                            ? t('common:actions.next_trial')
                            : t('common:actions.start_session') })), phase === 'feedback' && (_jsx("button", { type: "button", onClick: handleNext, className: "inline-flex h-12 w-full items-center justify-center bg-cyan-core font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-surface transition active:scale-[0.985] hover:bg-cyan-network", children: isLast ? t('common:actions.start_session') : t('common:actions.next_trial') })), _jsx("button", { type: "button", onClick: onSkip, className: "text-[12px] text-cyan-text-secondary underline-offset-4 hover:underline", children: t('common:actions.skip_demo') })] })] }));
}
//# sourceMappingURL=demo.js.map